"use client";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Terminal } from "lucide-react";
import { useApi } from "@/lib/hooks/use-api";
import { RequireAuth } from "@/components/auth";
import { flagFor } from "@/lib/range/templates";
import type { Lab } from "@/lib/types";
import type { LabHealth } from "@/lib/range/types";

interface Line {
  kind: "in" | "out" | "err";
  text: string;
}

const WELCOME = [
  "CyberAlem Range Terminal (mock) — изолированная рабочая среда",
  "Введите 'help' для списка команд.",
];

export function RangeTerminalPage({ runId }: { runId: string }) {
  return (
    <RequireAuth>
      <RangeTerminal runId={runId} />
    </RequireAuth>
  );
}

function RangeTerminal({ runId }: { runId: string }) {
  const lab = useApi<Lab>(`/labs/${encodeURIComponent(runId)}`);
  const health = useApi<LabHealth>(
    `/labs/${encodeURIComponent(runId)}/health`,
    5000,
  );
  const [lines, setLines] = useState<Line[]>(WELCOME.map((text) => ({
    kind: "out",
    text,
  })));
  const [input, setInput] = useState("");
  const [history, setHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const endRef = useRef<HTMLDivElement>(null);
  const host =
    typeof window !== "undefined" ? window.location.host : "127.0.0.1:3000";

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [lines]);

  const student = lab.data?.student ?? "student";
  const hostname = `kali-${runId.toLowerCase()}`;
  const machines = health.data?.machines ?? [];
  const machineUp = (name: string) =>
    machines.find((m) => m.name === name)?.status === "UP";

  function runCommand(raw: string): Line[] {
    const cmd = raw.trim();
    if (!cmd) return [];
    const [head, ...rest] = cmd.split(/\s+/);
    const out: Line[] = [{ kind: "in", text: cmd }];
    const push = (text: string, kind: "out" | "err" = "out") =>
      out.push({ kind, text });
    const blocked = (reason: string) =>
      push(`Ошибка: ${reason} — запрос запрещён политикой изоляции.`, "err");

    switch (head.toLowerCase()) {
      case "help":
        push(
          [
            "help            — этот список",
            "whoami, id      — текущий пользователь",
            "hostname, uname -a",
            "ls, cat /flag   — файлы рабочей станции",
            "nmap -sn        — сканирование подсети лаборатории",
            "curl <host>     — доступ к машинам лаборатории",
            "policy          — активные политики изоляции",
            "clear           — очистить терминал",
          ].join("\n"),
        );
        break;
      case "whoami":
        push(student.split(" ")[0].toLowerCase() || "student");
        break;
      case "id":
        push(`uid=1000(${student.split(" ")[0].toLowerCase() || "student"}) gid=1000 groups=1000,4(adm),24(cdrom),27(sudo),30(dip)`);
        break;
      case "hostname":
        push(hostname);
        break;
      case "uname":
        push(`Linux ${hostname} 6.6.0-cyberalem x86_64 GNU/Linux (mock image)`);
        break;
      case "ls":
        push("desktop  notes.txt  tools/  — рабочая станция (Kali, mock)");
        break;
      case "cat":
        if (rest.join(" ").includes("flag")) {
          push(flagFor());
        } else if (rest.join(" ").includes("notes")) {
          push("Задание: найти флаг на web-сервере, изучить robots.txt и логи nginx.");
        } else push(`cat: ${rest[0] ?? "файл"}: нет такого файла`, "err");
        break;
      case "nmap":
      case "ip": {
        const subnet = health.data
          ? machines.map((m, i) => {
              const ipAddr = `172.16.${170 + (runId.length % 10)}.${i + 2}`;
              const state =
                m.status === "UP" ? "up" : "down";
              return `${ipAddr}   ${m.name.padEnd(8)} (${m.role}, ${state})`;
            })
          : [];
        if (!subnet.length)
          push("Сеть лаборатории ещё не готова…", "err");
        else {
          push(`Подсеть лаборатории: только машины этого запуска (run ${runId})`);
          push(subnet.join("\n"));
        }
        break;
      }
      case "curl":
      case "wget":
      case "ping": {
        const target = (rest[0] ?? "").replace(/^https?:\/\//, "").split("/")[0];
        const labMachine = machines.find(
          (m) => m.name === target || target.includes(m.name),
        );
        if (labMachine) {
          if (!machineUp(labMachine.name)) {
            push(
              `connect: ${labMachine.name} — служба недоступна (статус DOWN). Попробуйте «Сбросить лабораторию».`,
              "err",
            );
          } else {
            push(
              labMachine.http_ok
                ? `HTTP/1.1 200 OK — ${labMachine.name} отвечает (lab gateway)`
                : `OK — ${labMachine.name} достижима внутри лаборатории`,
            );
          }
        } else if (
          /(\d{1,3}\.){3}\d{1,3}|[a-z0-9.-]+\.(com|ru|net|org|io|gov|edu)/.test(target)
        ) {
          blocked("LAB Internet DENY (нет внешнего выхода у лаборатории)");
        } else {
          blocked("LAB → other LABs / University LAN / Platform DENY (изоляция запусков)");
        }
        break;
      }
      case "policy":
      case "show":
        push(
          (health.data?.policies ?? [
            "LAB Internet: DENY",
            "LAB University LAN: DENY",
            "LAB → other LABs: DENY",
            "LAB Platform DB: DENY",
            "LAB Proxmox API: DENY",
          ]).join("\n"),
        );
        break;
      case "psql":
      case "psql3":
        blocked("LAB Platform DB DENY");
        break;
      case "kubectl":
        blocked("LAB Kubernetes API DENY");
        break;
      case "clear":
        setLines([]);
        return [];
      default:
        push(`${head}: команда не найдена в mock-окружении (help — список команд)`, "err");
    }
    return out;
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const value = input;
    setInput("");
    if (value.trim()) {
      setHistory((h) => [value, ...h].slice(0, 50));
      setHistoryIndex(-1);
      setLines((prev) => [...prev, ...runCommand(value)]);
    }
  }

  const ready = health.data?.ready;
  return (
    <div className="range-terminal">
      <div className="range-terminal-header">
        <span>
          <Terminal size={16} /> {hostname}
        </span>
        <span className="muted">
          run {runId}
          {lab.data ? ` · ${lab.data.student} · ${lab.data.scenario}` : ""}
          {ready === false ? " · лаборатория не полностью готова" : ""}
          {` · origin ${host} · provider mock`}
        </span>
      </div>
      <div className="range-terminal-body mono">
        {lines.map((line, i) => (
          <div key={i} className={line.kind === "err" ? "term-err" : ""}>
            {line.kind === "in" ? (
              <span>
                <span className="term-prompt">
                  {student.split(" ")[0]?.toLowerCase() || "student"}@{hostname}
                  :~$
                </span>{" "}
                {line.text}
              </span>
            ) : (
              <span className="term-pre" style={{ whiteSpace: "pre-wrap" }}>
                {line.text}
              </span>
            )}
          </div>
        ))}
        <form onSubmit={submit} className="term-input-row">
          <span className="term-prompt">
            {student.split(" ")[0]?.toLowerCase() || "student"}@{hostname}:~$
          </span>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowUp") {
                e.preventDefault();
                const next = Math.min(historyIndex + 1, history.length - 1);
                if (history[next]) {
                  setHistoryIndex(next);
                  setInput(history[next]);
                }
              } else if (e.key === "ArrowDown") {
                e.preventDefault();
                const next = historyIndex - 1;
                setHistoryIndex(next);
                setInput(next >= 0 ? history[next] : "");
              }
            }}
            autoComplete="off"
            spellCheck={false}
            aria-label="Команда терминала"
          />
        </form>
        <div ref={endRef} />
      </div>
    </div>
  );
}
